<template>
  <div class="admin-sessions">
    <!-- 操作按钮区域 -->
    <div class="action-section">
      <el-card>
        <div class="action-buttons">
          <el-button type="primary" @click="handleAdd">
            <el-icon><Plus /></el-icon>
            新增 Session
          </el-button>
          <el-button type="success" @click="handleRefresh">
            <el-icon><Refresh /></el-icon>
            刷新
          </el-button>
        </div>
      </el-card>
    </div>

    <!-- 数据表格 - 按日期分组展示 -->
    <div class="table-section">
      <el-card v-loading="loading">
        <div v-if="tableData.length === 0" class="empty-text">暂无数据</div>
        <div v-for="dateGroup in tableData" :key="dateGroup.date" class="date-group">
          <!-- 日期标题 -->
          <div class="date-header">
            <span class="date-title">{{ formatDate(dateGroup.date) }}</span>
            <span class="date-text" v-if="getDateText(dateGroup)">{{ getDateText(dateGroup) }}</span>
          </div>
          
          <!-- 三个 Session 类型 -->
          <div class="session-cards">
            <!-- OPENING SESSION -->
            <div class="session-card" :class="{ 'empty': !dateGroup.sessions.OPENING_SESSION }">
              <div class="session-type-tag opening">OPENING SESSION</div>
              <div v-if="dateGroup.sessions.OPENING_SESSION" class="session-content">
                <div class="host" v-if="dateGroup.sessions.OPENING_SESSION.host">
                  主持人：{{ dateGroup.sessions.OPENING_SESSION.host }}
                </div>
                <div class="schedules-count">
                  日程数量：<el-badge :value="dateGroup.sessions.OPENING_SESSION.schedules?.length || 0" type="primary" />
                </div>
                <div class="actions">
                  <el-button type="info" size="small" @click="handleViewSchedules(dateGroup.sessions.OPENING_SESSION)">查看日程</el-button>
                  <el-button type="primary" size="small" @click="handleEdit(dateGroup.sessions.OPENING_SESSION)">编辑</el-button>
                  <el-button type="danger" size="small" @click="handleDelete(dateGroup.sessions.OPENING_SESSION)">删除</el-button>
                </div>
              </div>
              <div v-else class="session-empty">
                <el-button type="primary" size="small" @click="handleAddWithType(dateGroup.date, 'OPENING_SESSION')">创建</el-button>
              </div>
            </div>

            <!-- MORNING SESSION -->
            <div class="session-card" :class="{ 'empty': !dateGroup.sessions.MORNING_SESSION }">
              <div class="session-type-tag morning">MORNING SESSION</div>
              <div v-if="dateGroup.sessions.MORNING_SESSION" class="session-content">
                <div class="host" v-if="dateGroup.sessions.MORNING_SESSION.host">
                  主持人：{{ dateGroup.sessions.MORNING_SESSION.host }}
                </div>
                <div class="schedules-count">
                  日程数量：<el-badge :value="dateGroup.sessions.MORNING_SESSION.schedules?.length || 0" type="primary" />
                </div>
                <div class="actions">
                  <el-button type="info" size="small" @click="handleViewSchedules(dateGroup.sessions.MORNING_SESSION)">查看日程</el-button>
                  <el-button type="primary" size="small" @click="handleEdit(dateGroup.sessions.MORNING_SESSION)">编辑</el-button>
                  <el-button type="danger" size="small" @click="handleDelete(dateGroup.sessions.MORNING_SESSION)">删除</el-button>
                </div>
              </div>
              <div v-else class="session-empty">
                <el-button type="primary" size="small" @click="handleAddWithType(dateGroup.date, 'MORNING_SESSION')">创建</el-button>
              </div>
            </div>

            <!-- AFTERNOON SESSION -->
            <div class="session-card" :class="{ 'empty': !dateGroup.sessions.AFTERNOON_SESSION }">
              <div class="session-type-tag afternoon">AFTERNOON SESSION</div>
              <div v-if="dateGroup.sessions.AFTERNOON_SESSION" class="session-content">
                <div class="host" v-if="dateGroup.sessions.AFTERNOON_SESSION.host">
                  主持人：{{ dateGroup.sessions.AFTERNOON_SESSION.host }}
                </div>
                <div class="schedules-count">
                  日程数量：<el-badge :value="dateGroup.sessions.AFTERNOON_SESSION.schedules?.length || 0" type="primary" />
                </div>
                <div class="actions">
                  <el-button type="info" size="small" @click="handleViewSchedules(dateGroup.sessions.AFTERNOON_SESSION)">查看日程</el-button>
                  <el-button type="primary" size="small" @click="handleEdit(dateGroup.sessions.AFTERNOON_SESSION)">编辑</el-button>
                  <el-button type="danger" size="small" @click="handleDelete(dateGroup.sessions.AFTERNOON_SESSION)">删除</el-button>
                </div>
              </div>
              <div v-else class="session-empty">
                <el-button type="primary" size="small" @click="handleAddWithType(dateGroup.date, 'AFTERNOON_SESSION')">创建</el-button>
              </div>
            </div>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 新增/编辑弹窗 -->
    <el-dialog
      v-model="formVisible"
      :title="isEdit ? '编辑 Session' : '新增 Session'"
      width="500px"
      :close-on-click-modal="false"
    >
      <el-form ref="formRef" :model="form" :rules="formRules" label-width="100px">
        <el-form-item label="日期" prop="session_date">
          <el-date-picker
            v-model="form.session_date"
            type="date"
            placeholder="选择日期"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="类型" prop="session_type">
          <el-select v-model="form.session_type" placeholder="选择类型" style="width: 100%">
            <el-option label="OPENING SESSION" value="OPENING_SESSION" />
            <el-option label="MORNING SESSION" value="MORNING_SESSION" />
            <el-option label="AFTERNOON SESSION" value="AFTERNOON_SESSION" />
          </el-select>
        </el-form-item>
        <el-form-item label="主持人" prop="host">
          <el-input v-model="form.host" placeholder="请输入主持人姓名" />
        </el-form-item>
        <el-form-item label="日期文本" prop="date_text">
          <el-input v-model="form.date_text" placeholder="如：May 10th, 2026" />
        </el-form-item>
        <el-form-item label="排序序号" prop="sort_order">
          <el-input-number v-model="form.sort_order" :min="0" :max="9999" :step="1" style="width: 100%" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="formVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSave" :loading="saving">保存</el-button>
      </template>
    </el-dialog>

    <!-- 查看日程弹窗 -->
    <el-dialog
      v-model="schedulesVisible"
      :title="`Session 日程 - ${currentSession?.date_text || formatDate(currentSession?.session_date) || ''}`"
      width="900px"
      :close-on-click-modal="false"
    >
      <div class="schedules-header">
        <div class="session-info">
          <el-tag :type="getSessionTypeTag(currentSession?.session_type)" size="large">
            {{ getSessionTypeText(currentSession?.session_type) }}
          </el-tag>
          <span class="date-text" v-if="currentSession?.date_text">
            {{ currentSession.date_text }}
          </span>
          <span class="host-info" v-if="currentSession?.host">
            主持人：{{ currentSession.host }}
          </span>
        </div>
        <el-button type="primary" size="small" @click="handleAddSchedule">
          <el-icon><Plus /></el-icon>
          新增日程
        </el-button>
      </div>
      
      <el-table :data="currentSchedules" stripe border style="width: 100%" empty-text="暂无日程">
        <el-table-column prop="start_time" label="开始时间" width="120" align="center">
          <template #default="{ row }">
            {{ formatTime(row.start_time) }}
          </template>
        </el-table-column>
        <el-table-column prop="end_time" label="结束时间" width="120" align="center">
          <template #default="{ row }">
            {{ row.end_time ? formatTime(row.end_time) : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="time_description" label="时间描述" width="150" align="center">
          <template #default="{ row }">
            <div class="pre-wrap">{{ row.time_description || '-' }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="title" label="标题" min-width="200">
          <template #default="{ row }">
            <div class="pre-wrap">{{ row.title }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="speaker_name" label="演讲者" width="150">
          <template #default="{ row }">
            <div class="pre-wrap">{{ row.speaker_name || '-' }}</div>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" align="center" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleEditSchedule(row)">编辑</el-button>
            <el-button type="danger" size="small" @click="handleDeleteSchedule(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>

    <!-- 新增/编辑日程弹窗 -->
    <el-dialog
      v-model="scheduleFormVisible"
      :title="isEditSchedule ? '编辑日程' : '新增日程'"
      width="600px"
      :close-on-click-modal="false"
    >
      <el-form ref="scheduleFormRef" :model="scheduleForm" :rules="scheduleFormRules" label-width="100px">
        <el-form-item label="开始时间" prop="start_time">
          <el-time-picker
            v-model="scheduleForm.start_time"
            placeholder="选择开始时间"
            format="HH:mm"
            value-format="HH:mm"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="结束时间" prop="end_time">
          <el-time-picker
            v-model="scheduleForm.end_time"
            placeholder="选择结束时间（可选）"
            format="HH:mm"
            value-format="HH:mm"
            style="width: 100%"
            clearable
          />
        </el-form-item>
        <el-form-item label="时间描述" prop="time_description">
          <el-input
            v-model="scheduleForm.time_description"
            type="textarea"
            :rows="2"
            placeholder="如：(EST, 21/11/2025, 9:35-10:00 PM)"
          />
        </el-form-item>
        <el-form-item label="标题" prop="title">
          <el-input
            v-model="scheduleForm.title"
            type="textarea"
            :rows="2"
            placeholder="请输入日程标题"
          />
        </el-form-item>
        <el-form-item label="演讲者" prop="speaker_name">
          <el-input
            v-model="scheduleForm.speaker_name"
            type="textarea"
            :rows="2"
            placeholder="请输入演讲者姓名（可选）"
          />
        </el-form-item>
        <el-form-item label="演讲者介绍" prop="speaker_bio">
          <el-input
            v-model="scheduleForm.speaker_bio"
            type="textarea"
            :rows="3"
            placeholder="请输入演讲者介绍（可选）"
          />
        </el-form-item>
        <el-form-item label="排序序号" prop="sort_order">
          <el-input-number v-model="scheduleForm.sort_order" :min="0" :max="9999" :step="1" style="width: 100%" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="scheduleFormVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSaveSchedule" :loading="savingSchedule">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Refresh } from '@element-plus/icons-vue'
import { getSessions, createSession, updateSession, deleteSession } from '@/api/session'
import { getSchedulesBySession, createSchedule, updateSchedule, deleteSchedule } from '@/api/schedule'

export default {
  name: 'AdminSessions',
  components: {
    Plus,
    Refresh
  },
  setup() {
    // 响应式数据
    const loading = ref(false)
    const saving = ref(false)
    const savingSchedule = ref(false)
    const tableData = ref([])
    const formVisible = ref(false)
    const schedulesVisible = ref(false)
    const scheduleFormVisible = ref(false)
    const isEdit = ref(false)
    const isEditSchedule = ref(false)
    const currentSession = ref(null)
    const currentSchedules = ref([])
    const formRef = ref(null)
    const scheduleFormRef = ref(null)

    // 表单数据
    const form = reactive({
      session_date: '',
      session_type: '',
      host: '',
      date_text: '',
      sort_order: 0
    })

    // 日程表单数据
    const scheduleForm = reactive({
      session_id: null,
      start_time: '',
      end_time: '',
      time_description: '',
      title: '',
      speaker_name: '',
      speaker_bio: '',
      sort_order: 0
    })

    // 表单验证规则
    const formRules = {
      session_date: [
        { required: true, message: '请选择日期', trigger: 'change' }
      ],
      session_type: [
        { required: true, message: '请选择类型', trigger: 'change' }
      ]
    }

    // 日程表单验证规则
    const scheduleFormRules = {
      start_time: [
        { required: true, message: '请选择开始时间', trigger: 'change' }
      ]
      // 标题现在可以为空，不需要验证
    }

    // 获取数据
    const fetchData = async () => {
      loading.value = true
      try {
        const response = await getSessions({ sort_by: 'session_date', order: 'desc' })
        if (response.success) {
          tableData.value = response.data
        } else {
          ElMessage.error(response.message || '获取数据失败')
        }
      } catch (error) {
        console.error('获取 Session 列表失败:', error)
        ElMessage.error('获取数据失败')
      } finally {
        loading.value = false
      }
    }

    // 获取 Session 类型文本
    const getSessionTypeText = (type) => {
      const typeMap = {
        'OPENING_SESSION': 'OPENING SESSION',
        'MORNING_SESSION': 'MORNING SESSION',
        'AFTERNOON_SESSION': 'AFTERNOON SESSION'
      }
      return typeMap[type] || type
    }

    // 获取 Session 类型标签样式
    const getSessionTypeTag = (type) => {
      const typeMap = {
        'OPENING_SESSION': 'success',
        'MORNING_SESSION': 'primary',
        'AFTERNOON_SESSION': 'warning'
      }
      return typeMap[type] || 'info'
    }

    // 获取日期文本（从任意一个 session 中获取）
    const getDateText = (dateGroup) => {
      const sessions = dateGroup.sessions
      for (const type of ['OPENING_SESSION', 'MORNING_SESSION', 'AFTERNOON_SESSION']) {
        if (sessions[type]?.date_text) {
          return sessions[type].date_text
        }
      }
      return null
    }

    // 刷新
    const handleRefresh = () => {
      fetchData()
    }

    // 新增
    const handleAdd = () => {
      isEdit.value = false
      resetForm()
      formVisible.value = true
    }

    // 新增指定类型的 Session
    const handleAddWithType = (date, type) => {
      isEdit.value = false
      resetForm()
      form.session_date = date
      form.session_type = type
      formVisible.value = true
    }

    // 编辑
    const handleEdit = (row) => {
      isEdit.value = true
      currentSession.value = row
      Object.assign(form, {
        session_date: row.session_date,
        session_type: row.session_type,
        host: row.host || '',
        date_text: row.date_text || '',
        sort_order: row.sort_order || 0
      })
      formVisible.value = true
    }

    // 保存
    const handleSave = async () => {
      if (!formRef.value) return

      try {
        await formRef.value.validate()
        saving.value = true

        const formData = { ...form }
        let response

        if (isEdit.value) {
          response = await updateSession(currentSession.value.id, formData)
        } else {
          response = await createSession(formData)
        }

        if (response.success) {
          ElMessage.success('保存成功')
          formVisible.value = false
          fetchData()
        } else {
          ElMessage.error(response.message || '保存失败')
        }
      } catch (error) {
        console.error('保存失败:', error)
        ElMessage.error('保存失败')
      } finally {
        saving.value = false
      }
    }

    // 删除
    const handleDelete = async (row) => {
      try {
        await ElMessageBox.confirm(
          '确定要删除该 Session 吗？删除后将同时删除关联的所有日程。',
          '警告',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )

        const response = await deleteSession(row.id)
        if (response.success) {
          ElMessage.success('删除成功')
          fetchData()
        } else {
          ElMessage.error(response.message || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error)
          ElMessage.error('删除失败')
        }
      }
    }

    // 查看日程
    const handleViewSchedules = async (row) => {
      currentSession.value = row
      schedulesVisible.value = true
      await fetchSchedules(row.id)
    }

    // 获取日程列表
    const fetchSchedules = async (sessionId) => {
      try {
        const response = await getSchedulesBySession(sessionId, { sort_by: 'start_time', order: 'asc' })
        if (response.success) {
          currentSchedules.value = response.data
        } else {
          ElMessage.error(response.message || '获取日程失败')
        }
      } catch (error) {
        console.error('获取日程列表失败:', error)
        ElMessage.error('获取日程失败')
      }
    }

    // 新增日程
    const handleAddSchedule = () => {
      isEditSchedule.value = false
      resetScheduleForm()
      scheduleForm.session_id = currentSession.value.id
      scheduleFormVisible.value = true
    }

    // 编辑日程
    const handleEditSchedule = (row) => {
      isEditSchedule.value = true
      Object.assign(scheduleForm, {
        session_id: currentSession.value.id,
        start_time: row.start_time ? row.start_time.substring(0, 5) : '',
        end_time: row.end_time ? row.end_time.substring(0, 5) : '',
        time_description: row.time_description || '',
        title: row.title,
        speaker_name: row.speaker_name || '',
        speaker_bio: row.speaker_bio || '',
        sort_order: row.sort_order || 0
      })
      scheduleForm.currentId = row.id
      scheduleFormVisible.value = true
    }

    // 保存日程
    const handleSaveSchedule = async () => {
      if (!scheduleFormRef.value) return

      try {
        await scheduleFormRef.value.validate()
        savingSchedule.value = true

        const formData = { ...scheduleForm }
        delete formData.currentId

        let response

        if (isEditSchedule.value) {
          response = await updateSchedule(scheduleForm.currentId, formData)
        } else {
          response = await createSchedule(formData)
        }

        if (response.success) {
          ElMessage.success('保存成功')
          scheduleFormVisible.value = false
          await fetchSchedules(currentSession.value.id)
        } else {
          ElMessage.error(response.message || '保存失败')
        }
      } catch (error) {
        console.error('保存日程失败:', error)
        ElMessage.error('保存失败')
      } finally {
        savingSchedule.value = false
      }
    }

    // 删除日程
    const handleDeleteSchedule = async (row) => {
      try {
        await ElMessageBox.confirm(
          '确定要删除该日程吗？',
          '警告',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )

        const response = await deleteSchedule(row.id)
        if (response.success) {
          ElMessage.success('删除成功')
          await fetchSchedules(currentSession.value.id)
        } else {
          ElMessage.error(response.message || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error)
          ElMessage.error('删除失败')
        }
      }
    }

    // 重置表单
    const resetForm = () => {
      Object.assign(form, {
        session_date: '',
        session_type: '',
        host: '',
        date_text: '',
        sort_order: 0
      })
      if (formRef.value) {
        formRef.value.clearValidate()
      }
    }

    // 重置日程表单
    const resetScheduleForm = () => {
      Object.assign(scheduleForm, {
        session_id: null,
        start_time: '',
        end_time: '',
        time_description: '',
        title: '',
        speaker_name: '',
        speaker_bio: '',
        sort_order: 0
      })
      if (scheduleFormRef.value) {
        scheduleFormRef.value.clearValidate()
      }
    }

    // 格式化日期
    const formatDate = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    }

    // 格式化日期时间
    const formatDateTime = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    // 格式化时间
    const formatTime = (timeString) => {
      if (!timeString) return '-'
      return timeString.substring(0, 5)
    }

    // 组件挂载时获取数据
    onMounted(() => {
      fetchData()
    })

    return {
      loading,
      saving,
      savingSchedule,
      tableData,
      formVisible,
      schedulesVisible,
      scheduleFormVisible,
      isEdit,
      isEditSchedule,
      currentSession,
      currentSchedules,
      form,
      scheduleForm,
      formRef,
      scheduleFormRef,
      formRules,
      scheduleFormRules,
      fetchData,
      handleRefresh,
      handleAdd,
      handleAddWithType,
      handleEdit,
      handleSave,
      handleDelete,
      handleViewSchedules,
      handleAddSchedule,
      handleEditSchedule,
      handleSaveSchedule,
      handleDeleteSchedule,
      getSessionTypeText,
      getSessionTypeTag,
      getDateText,
      formatDate,
      formatDateTime,
      formatTime
    }
  }
}
</script>

<style scoped>
.admin-sessions {
  padding: 0;
}

.action-section {
  margin-bottom: 20px;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.table-section {
  margin-bottom: 20px;
}

.schedules-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 10px 0;
  border-bottom: 1px solid #ebeef5;
}

.session-info {
  display: flex;
  align-items: center;
  gap: 15px;
}

.host-info {
  color: #606266;
  font-size: 14px;
}

.date-text {
  color: #409eff;
  font-weight: 500;
  font-size: 14px;
}

.pre-wrap {
  white-space: pre-wrap;
  line-height: 1.5;
}

/* 日期分组样式 */
.date-group {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #ebeef5;
}

.date-group:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.date-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
  padding: 10px 0;
}

.date-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.date-header .date-text {
  color: #409eff;
  font-size: 14px;
  font-weight: 500;
}

.session-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.session-card {
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  padding: 15px;
  background-color: #fff;
  transition: all 0.3s;
}

.session-card:hover {
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.session-card.empty {
  background-color: #f5f7fa;
  border-style: dashed;
}

.session-type-tag {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 15px;
  padding: 5px 10px;
  border-radius: 4px;
  display: inline-block;
}

.session-type-tag.opening {
  background-color: #f0f9eb;
  color: #67c23a;
}

.session-type-tag.morning {
  background-color: #ecf5ff;
  color: #409eff;
}

.session-type-tag.afternoon {
  background-color: #fdf6ec;
  color: #e6a23c;
}

.session-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.session-content .host {
  color: #606266;
  font-size: 14px;
}

.session-content .schedules-count {
  color: #909399;
  font-size: 13px;
}

.session-content .actions {
  display: flex;
  gap: 5px;
  margin-top: 10px;
  flex-wrap: wrap;
}

.session-empty {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100px;
}

.empty-text {
  text-align: center;
  color: #909399;
  padding: 40px 0;
  font-size: 14px;
}

@media (max-width: 1200px) {
  .session-cards {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .session-cards {
    grid-template-columns: 1fr;
  }
}
</style>
